import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, MapPin, Briefcase, GraduationCap, Ruler, Heart, Star, Flag, Share2, CheckCircle2 } from 'lucide-react';
import { User } from '../types';
interface ProfileDetailModalProps {
  user: User;
  onClose: () => void;
  onLike: () => void;
  onPass: () => void;
  onSuperLike: () => void;
}
export function ProfileDetailModal({
  user,
  onClose,
  onLike,
  onPass,
  onSuperLike
}: ProfileDetailModalProps) {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const handleNextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentPhotoIndex < user.photos.length - 1) {
      setCurrentPhotoIndex(prev => prev + 1);
    }
  };
  const handlePrevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(prev => prev - 1);
    }
  };
  return <motion.div initial={{
    y: '100%'
  }} animate={{
    y: 0
  }} exit={{
    y: '100%'
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 200
  }} className="absolute inset-0 z-50 bg-white overflow-y-auto" data-id="element-302">
      {/* Photo Gallery */}
      <div className="relative h-[60vh] w-full" data-id="element-303">
        <img src={user.photos[currentPhotoIndex]} alt={user.name} className="w-full h-full object-cover" data-id="element-304" />

        {/* Navigation Zones */}
        <div className="absolute inset-0 flex" data-id="element-305">
          <div className="w-1/2 h-full" onClick={handlePrevPhoto} data-id="element-306" />
          <div className="w-1/2 h-full" onClick={handleNextPhoto} data-id="element-307" />
        </div>

        {/* Photo Indicators */}
        <div className="absolute top-4 left-4 right-4 flex gap-1 z-10" data-id="element-308">
          {user.photos.map((_, idx) => <div key={idx} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${idx === currentPhotoIndex ? 'bg-white' : 'bg-white/30'}`} data-id="element-309" />)}
        </div>

        {/* Close Button */}
        <button onClick={onClose} className="absolute bottom-[-24px] right-6 w-12 h-12 bg-rose-500 rounded-full flex items-center justify-center text-white shadow-lg z-20 hover:bg-rose-600 transition-colors" data-id="element-310">
          <X size={24} data-id="element-311" />
        </button>

        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60 pointer-events-none" data-id="element-312" />
      </div>

      {/* Content */}
      <div className="px-6 pt-8 pb-32" data-id="element-313">
        <div className="flex items-center gap-2 mb-1" data-id="element-314">
          <h1 className="text-3xl font-bold text-zinc-900" data-id="element-315">
            {user.name}, {user.age}
          </h1>
          {user.verified && <CheckCircle2 size={24} className="text-blue-500 fill-blue-50" data-id="element-316" />}
          {user.online && <div className="w-3 h-3 bg-green-500 rounded-full ml-auto" data-id="element-317" />}
        </div>

        <div className="flex items-center gap-2 text-zinc-500 mb-6 text-sm" data-id="element-318">
          {user.job && <span className="flex items-center gap-1" data-id="element-319">
              <Briefcase size={14} data-id="element-320" />
              {user.job}
            </span>}
          {user.distance && <span className="flex items-center gap-1" data-id="element-321">
              <MapPin size={14} data-id="element-322" />
              {user.distance} miles
            </span>}
        </div>

        <div className="border-t border-zinc-100 py-6" data-id="element-323">
          <h3 className="font-bold text-zinc-900 mb-3" data-id="element-324">About Me</h3>
          <p className="text-zinc-600 leading-relaxed" data-id="element-325">{user.bio}</p>
        </div>

        <div className="border-t border-zinc-100 py-6" data-id="element-326">
          <h3 className="font-bold text-zinc-900 mb-3" data-id="element-327">Interests</h3>
          <div className="flex flex-wrap gap-2" data-id="element-328">
            {user.interests.map(interest => <span key={interest} className="px-3 py-1.5 bg-zinc-100 text-zinc-600 rounded-full text-sm font-medium border border-zinc-200" data-id="element-329">
                {interest}
              </span>)}
          </div>
        </div>

        <div className="border-t border-zinc-100 py-6 space-y-4" data-id="element-330">
          <h3 className="font-bold text-zinc-900 mb-3" data-id="element-331">More Info</h3>
          {user.height && <div className="flex items-center gap-3 text-zinc-600" data-id="element-332">
              <Ruler size={20} className="text-zinc-400" data-id="element-333" />
              <span data-id="element-334">{user.height}</span>
            </div>}
          {user.education && <div className="flex items-center gap-3 text-zinc-600" data-id="element-335">
              <GraduationCap size={20} className="text-zinc-400" data-id="element-336" />
              <span data-id="element-337">{user.education}</span>
            </div>}
          {user.lookingFor && <div className="flex items-center gap-3 text-zinc-600" data-id="element-338">
              <Heart size={20} className="text-zinc-400" data-id="element-339" />
              <span data-id="element-340">Looking for {user.lookingFor}</span>
            </div>}
        </div>

        <div className="flex justify-center gap-8 py-8 border-t border-zinc-100" data-id="element-341">
          <button className="flex flex-col items-center gap-2 text-zinc-400 hover:text-rose-500 transition-colors" data-id="element-342">
            <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center" data-id="element-343">
              <Share2 size={20} data-id="element-344" />
            </div>
            <span className="text-xs font-medium uppercase" data-id="element-345">Share</span>
          </button>
          <button className="flex flex-col items-center gap-2 text-zinc-400 hover:text-red-500 transition-colors" data-id="element-346">
            <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center" data-id="element-347">
              <Flag size={20} data-id="element-348" />
            </div>
            <span className="text-xs font-medium uppercase" data-id="element-349">Report</span>
          </button>
        </div>
      </div>

      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 left-0 right-0 flex justify-center items-center gap-6 z-50 pointer-events-none" data-id="element-350">
        <div className="pointer-events-auto flex gap-6 p-2 bg-white/80 backdrop-blur-md rounded-full shadow-xl border border-white/20" data-id="element-351">
          <button onClick={() => {
          onPass();
          onClose();
        }} className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center text-rose-500 hover:bg-rose-50 transition-colors border border-zinc-100" data-id="element-352">
            <X size={28} data-id="element-353" />
          </button>

          <button onClick={() => {
          onSuperLike();
          onClose();
        }} className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors border border-zinc-100" data-id="element-354">
            <Star size={24} data-id="element-355" />
          </button>

          <button onClick={() => {
          onLike();
          onClose();
        }} className="w-14 h-14 rounded-full bg-rose-500 shadow-lg shadow-rose-500/30 flex items-center justify-center text-white hover:bg-rose-600 transition-colors" data-id="element-356">
            <Heart size={28} fill="currentColor" data-id="element-357" />
          </button>
        </div>
      </div>
    </motion.div>;
}